import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { settingsAPI } from '../api/settingsAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import InventoryItemCard from '../components/InventoryItemCard'
import QuantityPicker from '../components/QuantityPicker'
import { usePermissions } from '../contexts/PermissionContext'
import Select from 'react-select'
import '../styles/selectControl.css'

function InventoryMoveScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { inventoryItemId } = useParams()
    const [searchParams] = useSearchParams()
    const quantityRef = useRef(null)
    const [error, setError] = useState(null)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [systemSettings, setSystemSettings] = useState({
        enable_warehouse_transit: true // default to true for safety
    })
    const [selectedWarehouse, setSelectedWarehouse] = useState(null)
    const [inventoryItem, setInventoryItem] = useState({
        stock_item: null,
        warehouse: { _id: '' },
        location: '',
        new_location_id: '',
        quantity: '',
        comments: '',
        max_quantity: 0,
    })

    // Fetch system settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await settingsAPI.get();
                setSystemSettings(settings);
            } catch (error) {
                console.error('Error fetching system settings:', error);
                // Keep default value if fetch fails
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        async function fetchInventoryItem() {
            try {
                const item = await inventoryItemAPI.getById(inventoryItemId)
                if (!item) return;

                const matchingWarehouse = warehouses.find(w => w.name === item.warehouse_name)
                
                if (matchingWarehouse) {
                    setSelectedWarehouse(matchingWarehouse)
                    
                    const formattedItem = {
                        stock_item: item.stock_item,
                        warehouse: matchingWarehouse,
                        warehouse_name: item.warehouse_name,
                        location: item.location_name,
                        new_location_id: '',
                        quantity: '',
                        vendor: item.vendor_name,
                        manufacturer: item.manufacturer_name,
                        cost: item.cost,
                        notes: item.notes,
                        bb_date: item.bb_date,
                        lot: item.lot,
                        comments: item.comments || '',
                        max_quantity: item.quantity || 0,
                    }
                    
                    setInventoryItem(formattedItem)
                }
            } catch (error) {
                setError('Failed to load inventory item')
            }
        }

        if (inventoryItemId && warehouses.length > 0) {
            fetchInventoryItem()
        }
    }, [inventoryItemId, warehouses])

    useEffect(() => {
        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll()
                setWarehouses(response)
            } catch (error) {
                setError('Failed to load warehouses')
            }
        }
        fetchWarehouses()
    }, [])

    // Update locations when warehouse changes
    useEffect(() => {
        let isMounted = true;  // Add mounted check

        async function fetchLocations() {
            if (!selectedWarehouse) {
                setLocations([])
                return
            }

            try {
                const warehouseLocations = await locationAPI.getAll(selectedWarehouse._id, undefined, undefined, undefined, undefined, true)

                if (!isMounted) return;  // Don't update state if unmounted

                // If we're in the same warehouse, filter out the current location
                let filteredLocations;
                if (selectedWarehouse._id === inventoryItem.warehouse?._id) {
                    const currentLocation = warehouseLocations.find(l => l.name === inventoryItem.location)
                    filteredLocations = warehouseLocations.filter(l => l._id !== currentLocation?._id)
                } else {
                    filteredLocations = warehouseLocations
                }

                setLocations(filteredLocations)

                // Try to automatically select location ending with '-FLOOR'
                const floorLocation = filteredLocations.find(l => l.name.toUpperCase().endsWith('-FLOOR'))
                if (floorLocation) {
                    setInventoryItem(prev => ({ ...prev, new_location_id: floorLocation._id }))
                } else {
                    // Clear selected location when warehouse changes and no FLOOR location found
                    setInventoryItem(prev => ({ ...prev, new_location_id: '' }))
                }
            } catch (error) {
                if (isMounted) {  // Only set error if mounted
                    setError('Failed to load locations')
                }
            }
        }

        fetchLocations()

        return () => {
            isMounted = false;  // Cleanup on unmount
        }
    }, [selectedWarehouse?._id, inventoryItem.warehouse?._id, inventoryItem.location])  // Simplified dependencies

    const submitHandler = async (e) => {
        e.preventDefault()
        if (!inventoryItem.quantity || inventoryItem.quantity === '') {
            setError('Please enter a quantity')
            quantityRef.current?.focus()
            return
        }
        if (!inventoryItem.new_location_id) {
            setError('Please select a destination location')
            return
        }
        try {
            await inventoryItemAPI.move(inventoryItemId, inventoryItem.quantity, inventoryItem.new_location_id, inventoryItem.comments)
            
            // Check for returnTo query parameter first, then fall back to location state filters
            const returnTo = searchParams.get('returnTo')
            if (returnTo) {
                navigate(returnTo)
            } else {
                // Get the return filters from the location state if they exist
                const returnFilters = location.state?.returnFilters || ''
                
                // Add refresh parameter to trigger data refresh
                const separator = returnFilters.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                
                // Navigate back to inventory with the filters preserved and refresh trigger
                navigate(`/inventory${returnFilters}${refreshParam}`)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const warehouseOptions = warehouses
        .filter(w => !systemSettings.enable_warehouse_transit || w._id === inventoryItem.warehouse?._id)
        .map(warehouse => ({
            value: warehouse._id,
            label: warehouse.name
        }))

    const locationOptions = locations.map(location => ({
        value: location._id,
        label: location.name,
        item_count: location.item_count
    }))

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('inventory.move')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to move inventory items.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
            <Col style={{ maxWidth: '730px' }}>
            <h1>Move Inventory Item {inventoryItemId}</h1>
            {inventoryItem?.stock_item && (
                <InventoryItemCard inventoryItem={inventoryItem} />
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <Form.Label>Quantity*</Form.Label>
                <QuantityPicker
                    ref={quantityRef}
                    casePack={inventoryItem.stock_item?.case_pack || 1}
                    uom={inventoryItem.stock_item?.uom || ''}
                    value={inventoryItem.quantity}
                    onChange={(qty) => setInventoryItem(prev => ({...prev, quantity: qty}))}
                    required
                    max={inventoryItem.max_quantity}
                />

                {!systemSettings.enable_warehouse_transit && (
                    <Form.Group className="mb-3" controlId="warehouse">
                        <Form.Label>To Warehouse*:</Form.Label>
                        <Select
                            value={warehouseOptions.find(
                                option => option.value === selectedWarehouse?._id
                            )}
                            onChange={(selectedOption) => {
                                const warehouse = warehouses.find(w => w._id === selectedOption?.value)
                                setSelectedWarehouse(warehouse || null)
                            }}
                            options={warehouseOptions}
                            placeholder="Select Warehouse"
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </Form.Group>
                )}
                <Form.Group className="mb-4" controlId="location">
                    <Form.Label>To Location*:</Form.Label>
                    <Select
                        value={locationOptions.find(
                            option => option.value === inventoryItem.new_location_id
                        )}
                        onChange={(selectedOption) => {
                            setInventoryItem({
                                ...inventoryItem,
                                new_location_id: selectedOption?.value || ''
                            })
                        }}
                        options={locationOptions}
                        isDisabled={!selectedWarehouse}
                        placeholder="Select Location"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        formatOptionLabel={(option) => (
                            <span>
                                {option.label}
                                {option.item_count != null && (
                                    <span className={`location-badge${option.item_count === 0 ? ' is-empty' : ''}`}>
                                        {option.item_count === 0 ? 'Empty' : `${option.item_count} items`}
                                    </span>
                                )}
                            </span>
                        )}
                    />
                </Form.Group>
                
                <Form.Group className="mb-4" controlId="comments">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        value={inventoryItem.comments}
                        onChange={(e) => setInventoryItem({...inventoryItem, comments: e.target.value})}
                        placeholder="Enter notes (max 500 characters)"
                        autoComplete="off"
                        spellCheck="true"
                    />
                    <Form.Text className="text-muted">
                        {inventoryItem.comments?.length || 0}/500 characters
                    </Form.Text>
                </Form.Group>
                {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                )}
                <Button variant="success" type="submit" className="me-2">
                    Move
                </Button>

                <Button 
                    variant="secondary" 
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </Button>
            </Form>
            
            </Col>
            </Row>
        </Container>
    )
}

export default InventoryMoveScreen 